import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { NEW_CALL_PARKING } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputNumber from "@/design/forms/InputNumber";
import InputNumeric from "@/design/forms/InputNumeric";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viRectangleP } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, InputAdornment, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import callParkingReducer from "./callParkingReducer";

function CallParking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const musicOnHoldStore = useSelector((state) => state.domain.musicOnHolds);

  // Get general data
  useEffect(() => {
    Promise.resolve(Api.getMusicOnHolds());
  }, []);

  const {
    value: callParking,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_CALL_PARKING,
    reducer: callParkingReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 80], { message: "Name must be at most 80 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      timeout: number()
        .required({ message: "Timeout is is required." })
        .min(60, { message: "Timeout must be between 60 and 3600 seconds." })
        .max(3600, { message: "Timeout must be between 60 and 3600 seconds." }),
      rangeStart: number()
        .required({ message: "The first number is required." })
        .outside([100, 119], {
          message: "The first number cannot be between 100 and 119.",
        })
        .not(0, { message: "The first number cannot be 0." })
        .not(1711, { message: "The first number cannot be 1711." })
        .not(1712, { message: "The first number cannot be 1712." })
        .not(1722, { message: "The first number cannot be 1722." })
        .not(1733, { message: "The first number cannot be 1733." })
        .not(1813, { message: "The first number cannot be 1813." }),
      rangeEnd: number()
        .required({ message: "The last number is required." })
        .outside([100, 119], {
          message: "The last number cannot be between 100 and 119.",
        })
        .not(0, { message: "The last number cannot be 0." })
        .not(1711, { message: "The last number cannot be 1711." })
        .not(1712, { message: "The last number cannot be 1712." })
        .not(1722, { message: "The last number cannot be 1722." })
        .not(1733, { message: "The last number cannot be 1733." })
        .not(1813, { message: "The last number cannot be 1813." })
        .min(Number(callParking.rangeStart), {
          message: "The call parking cannot have more than 10 spaces.",
        })
        .max(Number(callParking.rangeStart) + 9, {
          message: "The call parking cannot have more than 10 spaces.",
        }),
    }),
    onSubmit: () => Api.storeCallParking(callParking),
    onSuccess: (response) => {
      if (!callParking.id) {
        dispatch({ type: "id", value: response.data.parkingLot.id });
        navigate(`../${response.data.parkingLot.id}`);
      }

      Promise.resolve(Api.getCallParkings());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === callParking.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_CALL_PARKING });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getCallParking({ id: Number(id) }))
      .then((response) => {
        if (!ignore) {
          const lotRange = response.data.parkingLot.spaceExtensions.reduce(
            (acc, curr) => {
              if (Number(curr.exten) > Number(acc.rangeEnd)) {
                acc.rangeEnd = curr.exten;
              }
              if (Number(curr.exten) < Number(acc.rangeStart)) {
                acc.rangeStart = curr.exten;
              }
              return acc;
            },
            {
              rangeEnd: response.data.parkingLot.spaceExtensions[0].exten,
              rangeStart: response.data.parkingLot.spaceExtensions[0].exten,
            },
          );

          dispatch({
            type: "set",
            value: { ...response.data.parkingLot, ...lotRange },
          });
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [callParking.id, dispatch, id]);

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getCallParkings()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${callParking.name || "Call parking"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={callParking.name} icon={viRectangleP} />
              </IconButton>
            ) : (
              <Avatar text={callParking.name} icon={viRectangleP} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={callParking.id || "nk"}
                isActionable={!!callParking?.id}
                onDeleteCheck={() => Api.deleteCallParking(callParking, false)}
                onDelete={() => Api.deleteCallParking(callParking, true)}
                name={`${callParking.name} (${callParking.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={callParking.name || "Unnamed call parking"}
          subheader={callParking.exten || "No number set"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={callParking.name} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="exten"
              label="Number"
              onChange={formHandler}
              value={callParking.exten}
              onBlur={() => {
                if (!callParking.rangeStart && !callParking.rangeEnd) {
                  dispatch({ type: "checkParkingRange" });
                  setSaveState(SaveState.UNSAVED);
                }
              }}
              error={errors}
              helperText="Number cannot be 0 or any Belgian emergency number."
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Settings">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="rangeStart"
              label="Parking range start"
              onChange={formHandler}
              value={callParking.rangeStart}
              error={errors}
              helperText="The first number in the call parking. Number cannot be 0 or any Belgian emergency number."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="rangeEnd"
              label="Parking range end"
              onChange={formHandler}
              value={callParking.rangeEnd}
              error={errors}
              helperText="The last number in the call parking. Number cannot be 0 or any Belgian emergency number. The range cannot be larger than 10 numbers."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputAutocomplete
              hasEmpty={null}
              id="mohId"
              label="Music on hold"
              onChange={formHandler}
              value={callParking.mohId ?? null}
              selectValue="id"
              selects={musicOnHoldStore.list}
              getOptionLabel={(option) => option.name}
              error={errors}
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Overflow">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumeric
              required
              id="timeout"
              label="Timeout"
              onChange={formHandler}
              value={callParking.timeout}
              min={60}
              max={3600}
              error={errors}
              helperText="How long it takes for a parked number to time-out."
              endAdornment={
                <InputAdornment position="end" sx={{ marginRight: 1 }}>
                  seconds
                </InputAdornment>
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              id="returnCallAfterTimeout"
              onChange={formHandler}
              states={[false, true]}
              value={callParking.returnCallAfterTimeout}
              primary="Return the call after timeout"
              secondary="When the timeout is over, start calling the user again."
              error={errors}
            />
          </Grid>
          {!callParking.returnCallAfterTimeout && (
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <OverflowDrawer
                label="Overflow"
                value={callParking.timeoutOverflow}
                propertyName="timeoutOverflow"
                overflowHandler={(value) => formHandler("timeoutOverflow", value)}
                filter={{ exten: [callParking.exten], custom: [], sound: [] }}
                helperText="Set the destination if the call parking times out."
              />
            </Grid>
          )}
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default CallParking;
